<template>
  <v-row id="playDetails">
    <v-col cols="12">
      <h4 class="overline mb-1">Play Details</h4>
      <v-card flat color="bg_darkerish">
        <v-card-text class="py-6 px-6" v-if="selectedPlay && selectedPlay.id">
          <p class="text-h6 font-weight-light white--text d-flex">
            <span class="dw_green--text mr-2">{{ selectedPlay.id }}</span>
            {{ selectedPlay.name }}
            <v-spacer></v-spacer>
            <v-btn
              icon
              small
              @click="updateScenarioBuilder(['add', selectedPlay]); snackbar = true"
              :title="`Add ${selectedPlay.id} to the scenario`"
              class="mr-1">
              <v-icon color="dw_green" class="my-auto" v-text="mdiPlusCircleOutline" />
            </v-btn>
            <v-btn
              icon
              small
              @click="$emit('export-layer', selectedPlay.id)"
              :title="`Export ${selectedPlay.id} ATT&CK Navigator Layer`">
              <v-icon small color="dw_green" class="my-auto" v-text="mdiLayersTripleOutline" />
            </v-btn>
          </p>
          <p class="text-body-2 font-weight-light">
            {{ selectedPlay.description }}
          </p>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>CAPEC&trade; Attack Patterns</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedPlay.attackpatterns.length === 0"
              label
              outlined
              small
              color="grey">No Associated Attack Patterns</v-chip>
            <v-chip
              v-else
              v-for="capec in selectedPlay.attackpatterns"
              :key="`capec.${capec.id}`"
              label
              outlined
              small
              :color="capec.status === 'Deprecated' ? 'red' : 'dw_green'"
              @click="$router.push({ name: 'capec_view', params: { id: capec.id } })">
              CAPEC-{{ capec.id }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>MITRE ATT&amp;CK&reg; Techniques</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedPlay.techniques.length === 0"
              label
              outlined
              small
              color="grey">No Associated MITRE Techniques</v-chip>
            <v-chip
              v-else
              v-for="teq in selectedPlay.techniques"
              :key="`teq.${teq.id}`"
              label
              outlined
              small
              :color="(teq.revoked || teq.deprecated) ? 'red' : 'dw_green'"
              @click="$router.push({ name: 'technique_view', params: { id: teq.id } })">
              {{ teq.mitre_id }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>NIST SP 800-53 Controls</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedPlay.controls.length === 0"
              label
              outlined
              small
              color="grey">No Associated NIST SP 800-53 Controls</v-chip>
            <v-chip
              v-else
              v-for="ctrl in selectedPlay.controls"
              :key="`ctrl.${ctrl.sort_id}`"
              label
              outlined
              small
              color="dw_green"
              @click="$router.push({ name: 'control_view', params: { id: ctrl.id } })">
              {{ ctrl.label }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            <span>CWE&trade; Weaknesses</span>
          </p>
          <v-chip-group column>
            <v-chip
              v-if="selectedPlay.cwes.length === 0"
              label
              outlined
              small
              color="grey">No Associated Weaknesses</v-chip>
            <v-chip
              v-else
              v-for="cwe in selectedPlay.cwes"
              :key="`cwe.${cwe.id}`"
              label
              outlined
              small
              :color="cwe.status === 'Deprecated' ? 'red' : 'dw_green'"
              @click="$router.push({ name: 'cwe_view', params: { id: cwe.id } })">
              CWE-{{ cwe.id }}
            </v-chip>
          </v-chip-group>
          <v-divider class="my-2"></v-divider>

          <v-row no-gutters justify="start">
            <v-col cols="6" sm="4" md="3">
              <p class="text-subtitle-1 font-weight-regular white--text mb-0">
                Tools
              </p>
              <ul class="font-weight-light">
                <li v-if="selectedPlay.tools.length === 0">No Associated Tools</li>
                <li v-else v-for="tool in selectedPlay.tools" :key="`tool.${tool.id}`">
                  <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
                  {{ tool.name }}
                  <v-btn
                    icon
                    small
                    @click="selectedTool = tool"
                    class="mb-1 ml-2"
                    title="Show Tool Details">
                    <v-icon small color="dw_green" v-text="mdiChevronRight" />
                  </v-btn>
                </li>
              </ul>
            </v-col>
            <v-divider vertical class="my-3 mr-3" v-if="selectedTool"></v-divider>
            <v-col v-if="selectedTool" class="pa-2">
              <p class="text-body-2 font-weight-light white--text mb-1 py-auto">
                {{ selectedTool.name }}
                <v-btn
                  icon
                  x-small
                  @click="selectedTool = undefined"
                  class="mb-1 ml-2"
                  title="Hide Tool Details">
                  <v-icon x-small color="grey" v-text="mdiClose" />
                </v-btn>
              </p>
              <a
                v-if="selectedTool.url"
                :href="selectedTool.url"
                target="__blank"
                class="text-caption font-weight-light dw_green--text mb-0">
                {{ selectedTool.url }}
                <v-icon v-text="mdiOpenInNew" x-small class="ml-2" />
              </a>
              <p
                v-if="selectedTool.description"
                class="text-caption font-weight-light mb-0">
                {{ selectedTool.description }}
              </p>
            </v-col>
          </v-row>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Tags
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedPlay.tags.length === 0">No Associated Tags</li>
            <li v-else v-for="tag in selectedPlay.tags" :key="`tag.${tag.id}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ tag.name }}
              <v-btn
                icon
                small
                v-if="!filters.tags.some((fltr) => fltr.id === tag.id)"
                @click="$emit('filter-by-tag', 'add', tag);"
                class="mb-1 ml-2"
                title="Add Filter">
                <v-icon small color="dw_green" v-text="mdiFilterVariantPlus" />
              </v-btn>
              <v-btn
                icon
                small
                v-else
                @click="$emit('filter-by-tag', 'remove', tag);"
                class="mb-1 ml-2"
                title="Remove Filter">
                <v-icon small color="danger" v-text="mdiFilterVariantRemove" />
              </v-btn>
            </li>
          </ul>
          <v-divider class="my-2"></v-divider>

          <p class="text-subtitle-1 font-weight-regular white--text mb-0">
            Penetration Testing Objective(s)
          </p>
          <ul class="font-weight-light">
            <li v-if="selectedPlay.objectives.length === 0">No Associated Objectives</li>
            <li v-else v-for="objctv in selectedPlay.objectives" :key="`objctv.${objctv.id}`">
              <v-icon small class="mr-2 grey--text" v-text="mdiSquareMediumOutline" />
              {{ objctv.title }}
              <v-btn
                icon
                small
                v-if="!filters.objctvs.some((fltr) => fltr.id === objctv.id)"
                @click="$emit('filter-by-obj', 'add', objctv);"
                class="mb-1 ml-2"
                title="Add Filter">
                <v-icon small color="dw_green" v-text="mdiFilterVariantPlus" />
              </v-btn>
              <v-btn
                icon
                small
                v-else
                @click="$emit('filter-by-obj', 'remove', objctv);"
                class="mb-1 ml-2"
                title="Remove Filter">
                <v-icon small color="danger" v-text="mdiFilterVariantRemove" />
              </v-btn>
            </li>
          </ul>
        </v-card-text>
      </v-card>
    </v-col>
    <v-snackbar v-model="snackbar" top outlined timeout="2000" color="dw_green">
      <p class="mb-0 white--text">{{ selectedPlay.id }} Added</p>
      <template v-slot:action>
        <v-btn
          @click="snackbar = false"
          text
          small
          color="dw_green"
          class="my-auto"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  mdiPlusCircleOutline,
  mdiLayersTripleOutline,
  mdiSquareMediumOutline,
  mdiChevronRight,
  mdiClose,
  mdiOpenInNew,
  mdiFilterVariantPlus,
  mdiFilterVariantRemove
} from '@mdi/js';

export default {
  name: 'playDetails',
  props: ['filters'],
  data() {
    return {
      mdiPlusCircleOutline,
      mdiLayersTripleOutline,
      mdiSquareMediumOutline,
      mdiChevronRight,
      mdiClose,
      mdiOpenInNew,
      mdiFilterVariantPlus,
      mdiFilterVariantRemove,
      selectedTool: undefined,
      snackbar: false
    }
  },
  watch: {
    '$route.params': {
      async handler(params) {
        if (params.id) {
          await this.fetchPlayByID(params.id);
          this.selectedTool = undefined;
          this.$vuetify.goTo('#playDetails');
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters([
      'selectedPlay'
    ])
  },
  beforeRouteLeave(to, from, next) {
    if (to.name !== 'play_view') {
      this.clearSelected('play');
      this.selectedTool = undefined;
    }
    next();
  },
  methods: {
    ...mapActions([
      'fetchPlayByID',
      'updateScenarioBuilder'
    ]),
    ...mapMutations([
      'clearSelected',
      'throwError'
    ])
  }
};
</script>

<style scoped>
ul {
  padding: 0 0 0 0;
}
li {
  list-style-type: none;
}
</style>
